import { BackButton, PageContainer, PageHeader } from "@client/components/ui";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { Progress } from "@client/components/ui/progress";
import { useTriggerAuth } from "@client/lib/trigger-provider";
import { trpc } from "@client/lib/trpc-client";
import { createFileRoute } from "@tanstack/react-router";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import {
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	ChevronDown,
	ChevronRight,
	ExternalLink,
	FileSearch,
	Info,
	Loader2,
	RefreshCw,
	Wifi,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/sites/$siteId_/audit")({
	component: GeoAuditPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface CategoryScore {
	score: number;
	label: string;
	checks: Array<{
		name: string;
		passed: boolean;
		score: number;
		message: string;
	}>;
}

interface PageScore {
	url: string;
	score: number;
	categories: {
		contentStructure: CategoryScore;
		aiReadability: CategoryScore;
		listUsage: CategoryScore;
		contentDepth: CategoryScore;
		eatSignals: CategoryScore;
	};
	issues: Array<{
		signal: string;
		description: string;
		priority: "high" | "medium" | "low";
	}>;
}

interface AuditSummary {
	overallScore: number;
	totalPages: number;
	avgScore: number;
	categoryScores: {
		contentStructure: number;
		aiReadability: number;
		listUsage: number;
		contentDepth: number;
		eatSignals: number;
	};
	topIssues: Array<{
		signal: string;
		description: string;
		priority: "high" | "medium" | "low";
		affectedPages: number;
		percentage: number;
	}>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
	if (score >= 80) return "text-green-600 dark:text-green-400";
	if (score >= 60) return "text-yellow-600 dark:text-yellow-400";
	if (score >= 40) return "text-orange-500 dark:text-orange-400";
	return "text-red-600 dark:text-red-400";
}

function scoreBarColor(score: number): string {
	if (score >= 80) return "bg-green-500";
	if (score >= 60) return "bg-yellow-500";
	if (score >= 40) return "bg-orange-500";
	return "bg-red-500";
}

function priorityIcon(priority: "high" | "medium" | "low") {
	if (priority === "high")
		return <AlertCircle className="h-4 w-4 text-red-500" />;
	if (priority === "medium")
		return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
	return <Info className="h-4 w-4 text-blue-500" />;
}

function ScoreGauge({ score }: { score: number }) {
	const circumference = 2 * Math.PI * 54;
	const dashOffset = circumference - (score / 100) * circumference;

	const strokeColor =
		score >= 80
			? "#22c55e"
			: score >= 60
				? "#eab308"
				: score >= 40
					? "#f97316"
					: "#ef4444";

	return (
		<div className="relative flex h-36 w-36 items-center justify-center">
			<svg
				className="-rotate-90 absolute"
				width="144"
				height="144"
				viewBox="0 0 144 144"
				aria-label={`GEO score: ${score}`}
				role="img"
			>
				<circle
					cx="72"
					cy="72"
					r="54"
					fill="none"
					stroke="currentColor"
					strokeWidth="12"
					className="text-muted/20"
				/>
				<circle
					cx="72"
					cy="72"
					r="54"
					fill="none"
					stroke={strokeColor}
					strokeWidth="12"
					strokeDasharray={circumference}
					strokeDashoffset={dashOffset}
					strokeLinecap="round"
					style={{ transition: "stroke-dashoffset 0.8s ease" }}
				/>
			</svg>
			<div className="flex flex-col items-center">
				<span className={`font-bold text-4xl ${scoreColor(score)}`}>
					{score}
				</span>
				<span className="text-muted-foreground text-xs">Overall Score</span>
			</div>
		</div>
	);
}

function CategoryCard({ label, score }: { label: string; score: number }) {
	return (
		<div className="flex flex-col items-center gap-1 rounded-lg border bg-card p-4 text-center">
			<span className={`font-bold text-2xl ${scoreColor(score)}`}>{score}</span>
			<span className="text-muted-foreground text-xs leading-tight">
				{label}
			</span>
			<div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
				<div
					className={`h-full rounded-full transition-all ${scoreBarColor(score)}`}
					style={{ width: `${score}%` }}
				/>
			</div>
		</div>
	);
}

function CheckRow({ check }: { check: CategoryScore["checks"][0] }) {
	return (
		<div className="flex items-start gap-3 py-2">
			{check.passed ? (
				<CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
			) : (
				<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
			)}
			<div className="min-w-0 flex-1">
				<p className="font-medium text-sm">{check.name}</p>
				<p className="text-muted-foreground text-xs">{check.message}</p>
			</div>
		</div>
	);
}

function CategorySection({ category }: { category: CategoryScore }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="rounded-lg border">
			<button
				type="button"
				className="flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-muted/50"
				onClick={() => setOpen((o) => !o)}
			>
				<div className="flex items-center gap-3">
					<div className={`font-bold text-lg ${scoreColor(category.score)}`}>
						{category.score}
					</div>
					<div>
						<p className="text-left font-medium text-sm">{category.label}</p>
						<p className="text-left text-muted-foreground text-xs">
							{category.checks.filter((c) => c.passed).length}/
							{category.checks.length} checks passed
						</p>
					</div>
				</div>
				{open ? (
					<ChevronDown className="h-4 w-4 text-muted-foreground" />
				) : (
					<ChevronRight className="h-4 w-4 text-muted-foreground" />
				)}
			</button>

			{open && (
				<div className="divide-y border-t px-4">
					{category.checks.map((check) => (
						<CheckRow key={check.name} check={check} />
					))}
				</div>
			)}
		</div>
	);
}

function PageScoreRow({ page }: { page: PageScore }) {
	const [open, setOpen] = useState(false);
	const hostname = (() => {
		try {
			return new URL(page.url).pathname || "/";
		} catch {
			return page.url;
		}
	})();

	return (
		<div className="rounded-lg border">
			<button
				type="button"
				className="flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
				onClick={() => setOpen((o) => !o)}
			>
				<span
					className={`w-10 shrink-0 font-bold text-base ${scoreColor(page.score)}`}
				>
					{page.score}
				</span>
				<span
					className="min-w-0 flex-1 truncate text-left text-sm"
					title={page.url}
				>
					{hostname}
				</span>
				<span className="shrink-0 text-muted-foreground text-xs">
					{page.issues.filter((i) => i.priority === "high").length} issues
				</span>
				{open ? (
					<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
				) : (
					<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
				)}
			</button>

			{open && (
				<div className="space-y-2 border-t px-4 py-3">
					<a
						href={page.url}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 text-blue-600 text-xs hover:underline"
					>
						{page.url} <ExternalLink className="h-3 w-3" />
					</a>
					<div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
						{Object.entries(page.categories).map(([, cat]) => (
							<div key={cat.label} className="text-xs">
								<span className={`font-semibold ${scoreColor(cat.score)}`}>
									{cat.score}{" "}
								</span>
								<span className="text-muted-foreground">{cat.label}</span>
							</div>
						))}
					</div>
					{page.issues.length > 0 && (
						<div className="mt-2 space-y-1">
							{page.issues.slice(0, 3).map((issue) => (
								<div
									key={issue.signal}
									className="flex items-start gap-2 text-xs"
								>
									{priorityIcon(issue.priority)}
									<span className="text-muted-foreground">
										{issue.description}
									</span>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}

// ── Main component ────────────────────────────────────────────────────────────

function GeoAuditPage() {
	const { siteId } = Route.useParams();
	const trpcUtils = trpc.useUtils();
	const [auditId, setAuditId] = useState<string | null>(null);
	const [realtimeReady, setRealtimeReady] = useState(false);
	const { setAccessToken } = useTriggerAuth();

	// Fetch site info
	const site = trpc.sites.getById.useQuery(
		{ id: siteId },
		{ refetchOnWindowFocus: false },
	);

	// Fetch latest audit
	const latestAudit = trpc.geoAudit.getLatestAudit.useQuery(
		{ siteId },
		{ refetchOnWindowFocus: false },
	);

	// Poll active audit status
	const activeAuditId =
		auditId ??
		(latestAudit.data?.status === "processing" ||
		latestAudit.data?.status === "queued"
			? latestAudit.data.id
			: null);
	const auditStatus = trpc.geoAudit.getAuditStatus.useQuery(
		{ auditId: activeAuditId ?? "" },
		{
			enabled: !!activeAuditId,
			refetchInterval: (query) => {
				const status = query.state.data?.status;
				if (
					!realtimeReady &&
					(status === "processing" || status === "queued")
				) {
					return 5000;
				}
				return false;
			},
		},
	);

	// Set realtime token
	useEffect(() => {
		const token =
			auditStatus.data?.publicAccessToken ??
			latestAudit.data?.publicAccessToken;
		if (token?.trim()) {
			setAccessToken(token);
			setRealtimeReady(true);
		}
	}, [
		auditStatus.data?.publicAccessToken,
		latestAudit.data?.publicAccessToken,
		setAccessToken,
	]);

	// Realtime run subscription
	const triggerRunId =
		auditStatus.data?.triggerRunId ?? latestAudit.data?.triggerRunId;
	const syncAudit = trpc.geoAudit.syncAuditFromRealtime.useMutation();

	const { run: realtimeRun } = useRealtimeRun(triggerRunId ?? "", {
		enabled: !!(realtimeReady && triggerRunId),
		onComplete: async (run) => {
			// Save results via tRPC (reliable fallback if callInternalAPI was skipped)
			if (activeAuditId && run.output) {
				try {
					await syncAudit.mutateAsync({
						auditId: activeAuditId,
						output: run.output,
					});
				} catch {
					// Already saved by callInternalAPI — ignore
				}
			}
			toast.success("GEO Audit completed!");
			await Promise.all([
				trpcUtils.geoAudit.getLatestAudit.invalidate({ siteId }),
				trpcUtils.geoAudit.getAuditStatus.invalidate(),
				trpcUtils.credits.invalidate(),
			]);
			setAuditId(null);
		},
	});

	// Start audit mutation
	const startAudit = trpc.geoAudit.startAudit.useMutation({
		onSuccess: (data) => {
			toast.success("GEO Audit started!");
			setAuditId(data.auditId);
			trpcUtils.credits.invalidate();
		},
		onError: (err) => toast.error(err.message || "Failed to start audit"),
	});

	const handleStartAudit = () => {
		startAudit.mutate({ siteId });
	};

	// Determine what to display
	const activeAudit = auditId ? auditStatus.data : latestAudit.data;

	const isProcessing =
		activeAudit?.status === "queued" ||
		activeAudit?.status === "processing" ||
		startAudit.isPending;

	const isCompleted = activeAudit?.status === "completed";
	const isFailed = activeAudit?.status === "failed";

	const summary: AuditSummary | null = isCompleted
		? (activeAudit?.summary ?? null)
		: null;
	const pageScores: PageScore[] = isCompleted
		? (activeAudit?.pageScores ?? [])
		: [];

	// Realtime progress from Trigger.dev metadata
	const progress = realtimeRun?.metadata?.progress as number | undefined;
	const processedPages = realtimeRun?.metadata?.processedPages as
		| number
		| undefined;
	const totalPages = realtimeRun?.metadata?.totalPages as number | undefined;
	const statusLabel = realtimeRun?.metadata?.status as string | undefined;

	const hostname = site.data?.url
		? (() => {
				try {
					return new URL(site.data.url).hostname;
				} catch {
					return site.data.url;
				}
			})()
		: "";

	if (site.isLoading) {
		return (
			<PageContainer maxWidth="wide">
				<BackButton to={`/sites/${siteId}`} label="Back to site" />
				<div className="flex items-center justify-center py-16">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer maxWidth="wide">
			<BackButton to={`/sites/${siteId}`} label="Back to site" />

			<div className="flex flex-wrap items-start justify-between gap-4">
				<PageHeader
					title="GEO Audit Report"
					description={
						<div className="flex flex-wrap items-center gap-2">
							<span className="text-muted-foreground text-sm">
								{site.data?.url}
							</span>
							{realtimeReady && isProcessing && (
								<Badge
									variant="outline"
									className="gap-1 border-green-300 text-green-600"
								>
									<Wifi className="h-3 w-3" /> Live
								</Badge>
							)}
						</div>
					}
				/>

				<Button
					onClick={handleStartAudit}
					disabled={isProcessing}
					variant={isCompleted ? "outline" : "default"}
					size="sm"
					className="shrink-0"
				>
					{isProcessing ? (
						<>
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							Auditing…
						</>
					) : isCompleted ? (
						<>
							<RefreshCw className="mr-2 h-4 w-4" />
							Re-run Audit
						</>
					) : (
						<>
							<FileSearch className="mr-2 h-4 w-4" />
							Run GEO Audit
						</>
					)}
				</Button>
			</div>

			{/* ── Processing state ── */}
			{isProcessing && (
				<Card>
					<CardContent className="py-8">
						<div className="flex flex-col items-center gap-4 text-center">
							<Loader2 className="h-10 w-10 animate-spin text-primary" />
							<div>
								<p className="font-semibold text-lg">Analyzing your pages…</p>
								<p className="mt-1 text-muted-foreground text-sm capitalize">
									{statusLabel?.replace(/_/g, " ") ?? "Initializing audit"}
								</p>
							</div>
							{totalPages != null && totalPages > 0 && (
								<div className="w-full max-w-sm space-y-2">
									<Progress value={progress ?? 0} className="h-2" />
									<p className="text-muted-foreground text-xs">
										{processedPages ?? 0} / {totalPages} pages scored
									</p>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* ── Error state ── */}
			{isFailed && !isProcessing && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900 dark:bg-red-950/30">
					<div className="flex items-start gap-3">
						<AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
						<div>
							<p className="font-semibold text-red-900 dark:text-red-200">
								Audit failed
							</p>
							<p className="mt-1 text-red-800 text-sm dark:text-red-300">
								{activeAudit?.error ?? "An unexpected error occurred."}
							</p>
						</div>
					</div>
				</div>
			)}

			{/* ── Empty state (no audit yet) ── */}
			{!isProcessing && !isCompleted && !isFailed && !latestAudit.isLoading && (
				<Card>
					<CardContent className="py-12">
						<div className="flex flex-col items-center gap-4 text-center">
							<FileSearch className="h-12 w-12 text-muted-foreground/50" />
							<div>
								<p className="font-semibold text-xl">No audit yet</p>
								<p className="mt-1 max-w-md text-muted-foreground text-sm">
									Run a GEO audit to discover how well your site is optimized
									for AI systems like ChatGPT, Claude, and Perplexity.
								</p>
							</div>
							<Button
								onClick={handleStartAudit}
								disabled={startAudit.isPending}
							>
								<FileSearch className="mr-2 h-4 w-4" />
								Run GEO Audit
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* ── Results ── */}
			{isCompleted && summary && (
				<div className="space-y-6">
					{/* Score header */}
					<div className="flex flex-col gap-4 rounded-xl border bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
						<div className="space-y-1">
							<div className="flex items-center gap-2 text-muted-foreground text-sm">
								<span>{hostname}</span>
								<span>·</span>
								<span>{summary.totalPages} pages analyzed</span>
							</div>
							<p className="font-semibold text-xl">GEO Optimization Report</p>
							<p className="text-muted-foreground text-sm">
								Average page score:{" "}
								<span
									className={`font-semibold ${scoreColor(summary.avgScore)}`}
								>
									{summary.avgScore}
								</span>
							</p>
						</div>
						<ScoreGauge score={summary.overallScore} />
					</div>

					{/* Category scores */}
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
						<CategoryCard
							label="Content Structure"
							score={summary.categoryScores.contentStructure}
						/>
						<CategoryCard
							label="AI Readability"
							score={summary.categoryScores.aiReadability}
						/>
						<CategoryCard
							label="List & Tables"
							score={summary.categoryScores.listUsage}
						/>
						<CategoryCard
							label="Content Depth"
							score={summary.categoryScores.contentDepth}
						/>
						<CategoryCard
							label="E-E-A-T Signals"
							score={summary.categoryScores.eatSignals}
						/>
					</div>

					{/* Top priorities */}
					{summary.topIssues.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-base">Top Priorities</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								{summary.topIssues.map((issue, i) => (
									<div
										key={issue.signal}
										className="flex items-start gap-3 rounded-lg border bg-muted/30 p-3"
									>
										<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-xs">
											{i + 1}
										</span>
										{priorityIcon(issue.priority)}
										<div className="min-w-0 flex-1">
											<p className="font-medium text-sm">{issue.signal}</p>
											<p className="text-muted-foreground text-xs">
												{issue.description}
											</p>
											<p className="mt-1 text-muted-foreground text-xs">
												Affects{" "}
												<span className="font-medium text-foreground">
													{issue.affectedPages} page
													{issue.affectedPages > 1 ? "s" : ""}
												</span>{" "}
												({issue.percentage}%)
											</p>
										</div>
									</div>
								))}
							</CardContent>
						</Card>
					)}

					{/* Per-page category breakdown (site-wide averages) */}
					{pageScores.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-base">Category Details</CardTitle>
							</CardHeader>
							<CardContent className="space-y-2">
								{/* Use first page's category structure as template for site-wide checks */}
								{Object.values(pageScores[0].categories).map((cat) => {
									// Compute site average for each category
									const catKey = Object.entries(pageScores[0].categories).find(
										([, v]) => v.label === cat.label,
									)?.[0] as keyof PageScore["categories"] | undefined;

									if (!catKey) return null;

									const avgCatScore = Math.round(
										pageScores.reduce(
											(s, p) => s + p.categories[catKey].score,
											0,
										) / pageScores.length,
									);

									const avgCat: CategoryScore = {
										...cat,
										score: avgCatScore,
									};

									return <CategorySection key={cat.label} category={avgCat} />;
								})}
							</CardContent>
						</Card>
					)}

					{/* Per-page scores */}
					<Card>
						<CardHeader>
							<CardTitle className="text-base">
								Page Scores
								<span className="ml-2 font-normal text-muted-foreground text-sm">
									({pageScores.length} pages)
								</span>
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-2">
							{pageScores
								.sort((a, b) => a.score - b.score)
								.map((page) => (
									<PageScoreRow key={page.url} page={page} />
								))}
						</CardContent>
					</Card>

					<p className="text-center text-muted-foreground text-xs">
						Audit completed{" "}
						{activeAudit?.completedAt
							? new Date(activeAudit.completedAt).toLocaleString()
							: ""}
					</p>
				</div>
			)}
		</PageContainer>
	);
}
